import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

// ══════════════════════════════════════════════════════════════════════
// Types
// ══════════════════════════════════════════════════════════════════════

interface SpecItem {
  date: string;
  name: string;
  branch: string;
  status: "merged" | "in_progress" | "rejected" | string;
  alerts: string;
}

interface MissionControlResponse {
  snapshot_at: string;
  git?: {
    checked_out_branch: string | null;
    main_hash_short: string | null;
    branches: string[];
    branch_count: number;
    error?: string;
  };
  specs?: {
    total: number;
    merged: number;
    in_progress: number;
    with_alerts: number;
    items: SpecItem[];
    error?: string;
  };
  operations?: {
    audit_register: {
      total_findings: number;
      resolved: number;
      open: number;
      deferred: number;
    } | null;
    voiceprint_jobs: {
      total: number;
      pending: number;
      failed: number;
    } | null;
    batch_jobs_stuck: number | null;
    pipeline_stages_logged: number | null;
  };
  database: {
    items: number | null;
    segments: number | null;
    embedded_segments: number | null;
    speakers: number | null;
    speakers_with_bio: number | null;
    speakers_with_image: number | null;
    speakers_with_voiceprint: number | null;
    users: number | null;
    folders: number | null;
    chat_sessions: number | null;
    tables: number | null;
  };
  gaps: {
    untitled_items: number | null;
    no_thumbnail: number | null;
    no_summary: number | null;
    no_published_date: number | null;
    no_duration: number | null;
    unembedded_segments: number | null;
  };
  agents?: AgentsData | null;
}

interface AttentionItem {
  severity: "red" | "amber" | "gray";
  text: string;
  detail?: string;
}

interface SpeakerAuditCounters {
  coverage_gap: number;
  resolution_gap: number;
  visible_wrong_label_count: number;
  terminal_unidentifiable: number;
  as_of: string;
}

interface AgentSummary {
  total: number;
  active_24h: number;
  errors_24h: number;
  tasks_completed_24h: number;
}

interface AgentRow {
  name: string;
  current_task: string | null;
  current_branch: string | null;
  current_step: string | null;
  waiting_for: string | null;
  notes: string | null;
  last_active: string;
  status: "active" | "idle" | "stale" | "blocked";
}

interface AgentAttentionItem {
  agent: string;
  issue: string;
  severity: "error" | "warning";
  since: string;
  source: string;
}

interface AgentsData {
  summary: AgentSummary;
  agents: AgentRow[];
  attention: AgentAttentionItem[];
}

// ══════════════════════════════════════════════════════════════════════
// Helper Components
// ══════════════════════════════════════════════════════════════════════

function MetricCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number | null;
  subtitle?: string;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="mt-2 text-2xl font-semibold text-gray-900">
        {value !== null ? (typeof value === "number" ? value.toLocaleString() : value) : "—"}
      </p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

function ProgressBar({
  label,
  current,
  total,
  color = "indigo",
}: {
  label: string;
  current: number | null;
  total: number | null;
  color?: "indigo" | "green" | "amber" | "red";
}) {
  const pct = current !== null && total !== null && total > 0
    ? Math.round((current / total) * 100)
    : 0;
  
  const colorClasses = {
    indigo: "bg-indigo-600",
    green: "bg-green-600",
    amber: "bg-amber-500",
    red: "bg-red-600",
  };

  return (
    <div className="mb-4">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-700">{label}</span>
        <span className="text-gray-500">
          {current !== null ? current.toLocaleString() : "—"} / {total !== null ? total.toLocaleString() : "—"} ({pct}%)
        </span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-300`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function GapItem({
  label,
  count,
  severity,
}: {
  label: string;
  count: number | null;
  severity: "high" | "medium" | "low";
}) {
  if (count === null || count === 0) return null;
  
  const severityColors = {
    high: "bg-red-100 text-red-800 border-red-200",
    medium: "bg-amber-100 text-amber-800 border-amber-200",
    low: "bg-gray-100 text-gray-700 border-gray-200",
  };

  return (
    <div className={`flex justify-between items-center p-3 rounded-lg border ${severityColors[severity]}`}>
      <span className="font-medium">{label}</span>
      <span className="text-lg font-semibold">{count.toLocaleString()}</span>
    </div>
  );
}

function AttentionBadge({ item }: { item: AttentionItem }) {
  const colors = {
    red: "bg-red-100 text-red-800 border-red-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    gray: "bg-gray-100 text-gray-600 border-gray-300",
  };
  
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${colors[item.severity]}`}>
      <span className="text-lg">
        {item.severity === "red" ? "🔴" : item.severity === "amber" ? "🟠" : "⚪"}
      </span>
      <div>
        <p className="font-medium">{item.text}</p>
        {item.detail && <p className="text-sm opacity-75">{item.detail}</p>}
      </div>
    </div>
  );
}

function AuditCounterCard({
  title,
  value,
  unit,
  severity,
}: {
  title: string;
  value: number | null;
  unit: string;
  severity: "red" | "amber" | "green" | "info";
}) {
  const styles = {
    red:   { border: "border-red-300 bg-red-50",     text: "text-red-700" },
    amber: { border: "border-amber-300 bg-amber-50", text: "text-amber-700" },
    green: { border: "border-green-300 bg-green-50", text: "text-green-700" },
    info:  { border: "border-gray-200 bg-white",     text: "text-gray-900" },
  };
  const { border, text } = styles[severity];
  return (
    <div className={`rounded-lg border p-4 ${border}`}>
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className={`mt-2 text-2xl font-semibold ${text}`}>
        {value !== null ? value.toLocaleString() : "—"}
      </p>
      <p className="mt-1 text-xs text-gray-500">{unit}</p>
    </div>
  );
}

function BranchPill({ name, isMain }: { name: string; isMain: boolean }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
      isMain ? "bg-green-100 text-green-800" : "bg-purple-100 text-purple-800"
    }`}>
      {name}
    </span>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    merged: "bg-green-100 text-green-800",
    in_progress: "bg-amber-100 text-amber-800",
    rejected: "bg-red-100 text-red-800",
  };
  const labels: Record<string, string> = {
    merged: "Merged",
    in_progress: "In Progress",
    rejected: "Rejected",
  };
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[status] || "bg-gray-100 text-gray-800"}`}>
      {labels[status] || status}
    </span>
  );
}

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50"
      >
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Agent Operations Helpers
// ══════════════════════════════════════════════════════════════════════

function timeAgo(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const STATUS_ORDER: Record<string, number> = { blocked: 0, active: 1, idle: 2, stale: 3 };

function sortAgents(agents: AgentRow[]): AgentRow[] {
  return [...agents].sort((a, b) => {
    const ord = (STATUS_ORDER[a.status] ?? 4) - (STATUS_ORDER[b.status] ?? 4);
    if (ord !== 0) return ord;
    return new Date(b.last_active).getTime() - new Date(a.last_active).getTime();
  });
}

function AgentStatusDot({ status }: { status: AgentRow["status"] }) {
  const colors: Record<string, string> = {
    active: "bg-green-500",
    idle: "bg-gray-400",
    stale: "bg-red-500",
    blocked: "bg-amber-400",
  };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors[status] ?? "bg-gray-300"}`} />;
}

function AgentCard({ agent }: { agent: AgentRow }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-2">
      <div className="flex items-center gap-2">
        <AgentStatusDot status={agent.status} />
        <span className="font-semibold text-gray-900 capitalize">{agent.name}</span>
      </div>
      <p className="text-sm text-gray-600 truncate leading-snug" title={agent.current_task ?? undefined}>
        {agent.current_task || <span className="text-gray-400 italic">idle</span>}
      </p>
      {agent.waiting_for && (
        <span className="inline-block text-xs bg-amber-100 text-amber-800 border border-amber-200 rounded px-2 py-0.5 truncate max-w-full">
          Waiting: {agent.waiting_for}
        </span>
      )}
      <p className="text-xs text-gray-400">{timeAgo(agent.last_active)}</p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// Attention Logic
// ══════════════════════════════════════════════════════════════════════

function computeAttentionItems(data: MissionControlResponse): AttentionItem[] {
  const items: AttentionItem[] = [];
  
  // Git: open branches — only if git subtree present
  if (data.git && data.git.branch_count > 1) {
    const otherBranches = (data.git.branches ?? []).filter(b => b !== "main");
    const BRANCH_WARN_THRESHOLD = 20;
    items.push({
      severity: otherBranches.length >= BRANCH_WARN_THRESHOLD ? "amber" : "gray",
      text: `${otherBranches.length} open branch${otherBranches.length > 1 ? "es" : ""}`,
      detail: otherBranches.slice(0, 5).join(", ") + (otherBranches.length > 5 ? `… +${otherBranches.length - 5} more` : ""),
    });
  }
  
  // Specs: in progress — collapsed to single amber summary
  const inProgress = data.specs?.items?.filter(s => s.status === "in_progress") ?? [];
  if (inProgress.length > 0) {
    items.push({
      severity: "amber",
      text: `${inProgress.length} spec${inProgress.length > 1 ? "s" : ""} in progress`,
      detail: inProgress.map(s => s.branch).join(", "),
    });
  }
  
  // Specs: with alerts — only surface alerts with meaningful detail text
  const GENERIC_ALERT_PLACEHOLDERS = new Set(["warning", "alert", "error", "—", ""]);
  const withAlerts = data.specs?.items?.filter(
    s => s.alerts && !GENERIC_ALERT_PLACEHOLDERS.has(s.alerts.toLowerCase().trim())
  ) ?? [];
  withAlerts.forEach(spec => {
    items.push({
      severity: "red",
      text: `Spec "${spec.name}" had alert`,
      detail: spec.alerts,
    });
  });
  
  // Operations: open audit findings
  if (data.operations?.audit_register && data.operations.audit_register.open > 0) {
    items.push({
      severity: "amber",
      text: `${data.operations.audit_register.open} open audit findings`,
    });
  }
  
  // Operations: failed voiceprint jobs
  if (data.operations?.voiceprint_jobs && data.operations.voiceprint_jobs.failed > 0) {
    items.push({
      severity: "amber",
      text: `${data.operations.voiceprint_jobs.failed} failed voiceprint jobs`,
    });
  }
  
  // Operations: stuck batch jobs
  if (data.operations?.batch_jobs_stuck && data.operations.batch_jobs_stuck > 0) {
    items.push({
      severity: "red",
      text: `${data.operations.batch_jobs_stuck} stuck batch job${data.operations.batch_jobs_stuck > 1 ? "s" : ""}`,
      detail: "Processing for >30 minutes",
    });
  }
  
  // Data gaps
  const gaps = data.gaps;
  if (gaps.no_summary && gaps.no_summary > 10) {
    items.push({ severity: "amber", text: `${gaps.no_summary} items missing summary` });
  }
  if (gaps.no_thumbnail && gaps.no_thumbnail > 5) {
    items.push({ severity: "amber", text: `${gaps.no_thumbnail} items missing thumbnail` });
  }
  if (gaps.unembedded_segments && gaps.unembedded_segments > 100) {
    items.push({ severity: "amber", text: `${gaps.unembedded_segments.toLocaleString()} unembedded segments` });
  }
  
  return items;
}

// ══════════════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════════════

export default function AdminMissionControlView() {
  const [data, setData] = useState<MissionControlResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [auditCounters, setAuditCounters] = useState<SpeakerAuditCounters | null>(null);
  const [vpFailedTrend, setVpFailedTrend] = useState<"up" | "stable" | "down" | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    
    try {
      const res = await fetch(`${API_BASE}/admin/dashboard/mission-control`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      
      setData(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuditCounters = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/speaker-audit/counters`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) return;
      setAuditCounters(await res.json());
    } catch {
      // silent — counters are informational and non-blocking
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchAuditCounters();
    const id = setInterval(fetchAuditCounters, 60_000);
    return () => clearInterval(id);
  }, [fetchAuditCounters]);

  // Trend tracking for voiceprint failed count
  useEffect(() => {
    if (!data?.operations?.voiceprint_jobs) return;
    const current = data.operations.voiceprint_jobs.failed;
    const stored = sessionStorage.getItem("mc_vp_failed_prev");
    if (stored !== null) {
      const prev = parseInt(stored, 10);
      if (current > prev) setVpFailedTrend("up");
      else if (current < prev) setVpFailedTrend("down");
      else setVpFailedTrend("stable");
    }
    sessionStorage.setItem("mc_vp_failed_prev", String(current));
  }, [data?.operations?.voiceprint_jobs?.failed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Error: {error}</p>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  const db = data.database;
  const gaps = data.gaps;
  const attentionItems = computeAttentionItems(data);
  
  // Calculate derived metrics
  const embeddedPct = db.segments && db.embedded_segments !== null
    ? Math.round((db.embedded_segments / db.segments) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Mission Control</h2>
          <p className="text-sm text-gray-500">
            Snapshot: {new Date(data.snapshot_at).toLocaleString()}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* 1. Needs Attention */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Needs Attention</h3>
        {attentionItems.length === 0 ? (
          <p className="text-green-600 text-center py-4">✓ All clear — nothing needs attention</p>
        ) : (
          <div className="space-y-3">
            {attentionItems.map((item, i) => (
              <AttentionBadge key={i} item={item} />
            ))}
          </div>
        )}
      </div>

      {/* 2. Agent Operations — only rendered when backend returns agents key */}
      {"agents" in data && (
      <CollapsibleSection title="Agent Operations" defaultOpen={true}>
        {data.agents === null ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
            Agent operations data not available.
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <AuditCounterCard
                title="Total Agents"
                value={data.agents.summary.total}
                unit="registered"
                severity="info"
              />
              <AuditCounterCard
                title="Active (24h)"
                value={data.agents.summary.active_24h}
                unit="distinct agents"
                severity={
                  data.agents.summary.active_24h >= 8
                    ? "green"
                    : data.agents.summary.active_24h >= 4
                    ? "amber"
                    : "red"
                }
              />
              <AuditCounterCard
                title="Errors (24h)"
                value={data.agents.summary.errors_24h}
                unit="failed tasks"
                severity={
                  data.agents.summary.errors_24h === 0
                    ? "green"
                    : data.agents.summary.errors_24h <= 3
                    ? "amber"
                    : "red"
                }
              />
              <AuditCounterCard
                title="Tasks (24h)"
                value={data.agents.summary.tasks_completed_24h}
                unit="completed"
                severity="info"
              />
            </div>

            {/* Agent grid */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-3">Agents</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {sortAgents(data.agents.agents).map(agent => (
                  <AgentCard key={agent.name} agent={agent} />
                ))}
              </div>
            </div>

            {/* Attention items — only if non-empty */}
            {data.agents.attention.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">Attention Items</h4>
                <div className="space-y-2">
                  {data.agents.attention.map((item, i) => (
                    <div
                      key={i}
                      className={`rounded-lg border p-3 ${
                        item.severity === "error"
                          ? "border-red-300 bg-red-50"
                          : "border-amber-300 bg-amber-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900 capitalize">{item.agent}</span>
                        <span className="text-xs text-gray-500">{timeAgo(item.since)}</span>
                      </div>
                      <p className={`text-sm mt-0.5 ${item.severity === "error" ? "text-red-700" : "text-amber-700"}`}>
                        {item.issue}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CollapsibleSection>
      )}

      {/* 3. Repository State — only rendered when backend returns git subtree */}
      {data.git && (
      <CollapsibleSection title="Repository State" defaultOpen={true}>
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">Checked out:</span>
            <BranchPill 
              name={data.git.checked_out_branch || "unknown"} 
              isMain={data.git.checked_out_branch === "main"} 
            />
            {data.git.main_hash_short && (
              <span className="text-xs text-gray-400 font-mono">
                main @ {data.git.main_hash_short}
              </span>
            )}
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-2">All branches ({data.git.branch_count}):</p>
            <div className="flex flex-wrap gap-2">
              {data.git.branches.map(branch => (
                <BranchPill key={branch} name={branch} isMain={branch === "main"} />
              ))}
            </div>
          </div>
        </div>
      </CollapsibleSection>
      )}

      {/* 4. Spec Tracker — only rendered when backend returns specs subtree */}
      {data.specs && (
      <CollapsibleSection title="Spec Tracker" defaultOpen={false}>
        <div className="space-y-4">
          <div className="flex gap-4 text-sm">
            <span className="text-gray-500">Total: <strong>{data.specs.total}</strong></span>
            <span className="text-green-600">Merged: <strong>{data.specs.merged}</strong></span>
            <span className="text-amber-600">In Progress: <strong>{data.specs.in_progress}</strong></span>
            {data.specs.with_alerts > 0 && (
              <span className="text-red-600">With Alerts: <strong>{data.specs.with_alerts}</strong></span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Date</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Spec</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Branch</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Status</th>
                  <th className="text-left py-2 px-2 font-medium text-gray-500">Alerts</th>
                </tr>
              </thead>
              <tbody>
                {data.specs.items.map((spec, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="py-2 px-2 text-gray-600">{spec.date}</td>
                    <td className="py-2 px-2 text-gray-900">{spec.name}</td>
                    <td className="py-2 px-2">
                      <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{spec.branch}</code>
                    </td>
                    <td className="py-2 px-2"><StatusPill status={spec.status} /></td>
                    <td className="py-2 px-2">
                      {spec.alerts && spec.alerts !== "—" && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          {spec.alerts}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleSection>
      )}

      {/* 5. Database Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Database Health</h3>
        
        {/* Metric Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <MetricCard title="Items" value={db.items} />
          <MetricCard title="Segments" value={db.segments} />
          <MetricCard title="Speakers" value={db.speakers} />
          <MetricCard title="Users" value={db.users} />
          <MetricCard title="Folders" value={db.folders} />
          <MetricCard title="Chat Sessions" value={db.chat_sessions} />
          <MetricCard title="Tables" value={db.tables} />
          <MetricCard title="Embedded %" value={`${embeddedPct}%`} subtitle="of segments" />
        </div>

        {/* Progress Bars */}
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Coverage</h4>
          <ProgressBar
            label="Segment Embeddings"
            current={db.embedded_segments}
            total={db.segments}
            color="green"
          />
          <ProgressBar
            label="Speaker Bios"
            current={db.speakers_with_bio}
            total={db.speakers}
            color="indigo"
          />
          <ProgressBar
            label="Speaker Images"
            current={db.speakers_with_image}
            total={db.speakers}
            color="amber"
          />
          <ProgressBar
            label="Items with Summaries"
            current={db.items !== null && gaps.no_summary !== null ? db.items - gaps.no_summary : null}
            total={db.items}
            color="indigo"
          />
          <ProgressBar
            label="Items with Published Date"
            current={db.items !== null && gaps.no_published_date !== null ? db.items - gaps.no_published_date : null}
            total={db.items}
            color="amber"
          />
        </div>

        {/* Data Gaps */}
        <div>
          <h4 className="text-md font-medium text-gray-700 mb-3">Data Gaps</h4>
          <div className="space-y-3">
            <GapItem label="Untitled Items" count={gaps.untitled_items} severity="high" />
            <GapItem label="No Thumbnail" count={gaps.no_thumbnail} severity="medium" />
            <GapItem label="No Summary" count={gaps.no_summary} severity="medium" />
            <GapItem label="No Published Date" count={gaps.no_published_date} severity="low" />
            <GapItem label="No Duration" count={gaps.no_duration} severity="medium" />
            <GapItem label="Unembedded Segments" count={gaps.unembedded_segments} severity="high" />
            
            {/* Show "All clear" if no gaps */}
            {(gaps.untitled_items === 0 || gaps.untitled_items === null) &&
             (gaps.no_thumbnail === 0 || gaps.no_thumbnail === null) &&
             (gaps.no_summary === 0 || gaps.no_summary === null) &&
             (gaps.no_published_date === 0 || gaps.no_published_date === null) &&
             (gaps.no_duration === 0 || gaps.no_duration === null) &&
             (gaps.unembedded_segments === 0 || gaps.unembedded_segments === null) && (
              <p className="text-green-600 text-center py-4">✓ No data gaps detected</p>
            )}
          </div>
        </div>
      </div>

      {/* 6. Speaker Audit Counters */}
      <CollapsibleSection title="Speaker Audit" defaultOpen={true}>
        {auditCounters ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-400">
              As of {new Date(auditCounters.as_of).toLocaleString()} · auto-refreshes every 60 s
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <AuditCounterCard
                title="Coverage gap"
                value={auditCounters.coverage_gap}
                unit="items never audited"
                severity={auditCounters.coverage_gap === 0 ? "green" : "red"}
              />
              <AuditCounterCard
                title="Resolution gap"
                value={auditCounters.resolution_gap}
                unit="flags awaiting review"
                severity={
                  auditCounters.resolution_gap === 0
                    ? "green"
                    : auditCounters.resolution_gap <= 5
                    ? "amber"
                    : "red"
                }
              />
              <AuditCounterCard
                title="Visible wrong labels"
                value={auditCounters.visible_wrong_label_count}
                unit="cat-B mislabelled items"
                severity={
                  auditCounters.visible_wrong_label_count === 0
                    ? "green"
                    : auditCounters.visible_wrong_label_count <= 20
                    ? "amber"
                    : "red"
                }
              />
              <AuditCounterCard
                title="Terminal unidentifiable"
                value={auditCounters.terminal_unidentifiable}
                unit="confirmed unresolvable"
                severity="info"
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-2">Loading speaker audit counters…</p>
        )}
      </CollapsibleSection>

      {/* 7. Operations — only rendered when backend returns operations subtree */}
      {data.operations && (
      <CollapsibleSection title="Operations" defaultOpen={false}>
        <div className="space-y-4">
          {/* Audit Register */}
          {data.operations.audit_register && (
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Audit Register</h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MetricCard title="Total Findings" value={data.operations.audit_register.total_findings} />
                <MetricCard title="Resolved" value={data.operations.audit_register.resolved} />
                <MetricCard title="Open" value={data.operations.audit_register.open} />
                <MetricCard title="Deferred" value={data.operations.audit_register.deferred} />
              </div>
            </div>
          )}
          
          {/* Voiceprint Jobs */}
          {data.operations.voiceprint_jobs && (
            <div>
              <h4 className="text-md font-medium text-gray-700 mb-3">Voiceprint Jobs</h4>
              <div className="grid grid-cols-3 gap-3">
                <MetricCard title="Total" value={data.operations.voiceprint_jobs.total} />
                <MetricCard title="Pending" value={data.operations.voiceprint_jobs.pending} />
                <MetricCard
                  title="Failed"
                  value={data.operations.voiceprint_jobs.failed}
                  subtitle={
                    vpFailedTrend === "up" ? "↑ increasing since last refresh"
                    : vpFailedTrend === "down" ? "↓ decreasing since last refresh"
                    : vpFailedTrend === "stable" ? "→ stable since last refresh"
                    : undefined
                  }
                />
              </div>
            </div>
          )}
          
          {/* Other metrics */}
          <div className="grid grid-cols-2 gap-3">
            <MetricCard title="Stuck Batch Jobs" value={data.operations.batch_jobs_stuck} subtitle=">30 min processing" />
            <MetricCard title="Pipeline Stages Logged" value={data.operations.pipeline_stages_logged} />
          </div>
        </div>
      </CollapsibleSection>
      )}
    </div>
  );
}
